import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { demoAnswer, initialDemoState } from "./mockData";
import { createMockReply } from "./mockReplies";

const AipoContext = createContext(null);
const copyInitialState = () => JSON.parse(JSON.stringify(initialDemoState));
const makeId = (prefix) => `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const readDemoState = (storageKey) => {
  try {
    const saved = JSON.parse(localStorage.getItem(storageKey) || "null");
    if (Array.isArray(saved?.projects) && Array.isArray(saved?.chats)) {
      return {
        ...saved,
        chats: saved.chats.map((chat) => ({
          ...chat,
          connection: chat.connection === "empresa" ? "aipo" : chat.connection,
          messages: (chat.messages || []).map((message) => ({
            ...message,
            text: message.role === "assistant" && message.text?.startsWith("Esta é uma resposta demonstrativa.")
              ? demoAnswer.text
              : message.text,
            sources: message.sources?.map((source) => source.replace(/ · exemplo$/, "")),
          })),
        })),
      };
    }
  } catch {
    // An invalid preview cache should not prevent opening AIPO.
  }
  return copyInitialState();
};

export const AipoProvider = ({ identity, children }) => {
  const storageKey = `aipo-preview-${identity.clientId}-${identity.email || "usuario"}`;
  const [data, setData] = useState(() => readDemoState(storageKey));

  useEffect(() => {
    localStorage.setItem(storageKey, JSON.stringify(data));
  }, [data, storageKey]);

  const actions = useMemo(() => ({
    createProject(name, description = "") {
      const project = { id: makeId("projeto"), name: name.trim(), description: description.trim(), updatedAt: "Agora" };
      setData((current) => ({ ...current, projects: [project, ...current.projects] }));
      return project.id;
    },
    createChat(projectId, prompt = "", sources = [], connection = "aipo") {
      const text = prompt.trim();
      const chat = {
        id: makeId("chat"),
        projectId,
        title: text ? text.slice(0, 48) : "Nova conversa",
        updatedAt: "Agora",
        sources,
        connection,
        messages: text
          ? [{ id: makeId("pergunta"), role: "user", text }, {
              ...createMockReply(text),
              id: makeId("resposta"),
            }]
          : [],
      };
      setData((current) => ({ ...current, chats: [chat, ...current.chats] }));
      return chat.id;
    },
    sendMessage(chatId, prompt) {
      const text = prompt.trim();
      if (!text) return;
      setData((current) => ({
        ...current,
        chats: current.chats.map((chat) => {
          if (chat.id !== chatId) return chat;
          return {
            ...chat,
            title: chat.messages.length ? chat.title : text.slice(0, 48),
            updatedAt: "Agora",
            messages: [
              ...chat.messages,
              { id: makeId("pergunta"), role: "user", text },
              { ...createMockReply(text), id: makeId("resposta") },
            ],
          };
        }),
      }));
    },
    updateChatSettings(chatId, settings) {
      setData((current) => ({
        ...current,
        chats: current.chats.map((chat) =>
          chat.id === chatId ? { ...chat, ...settings } : chat
        ),
      }));
    },
  }), []);

  return <AipoContext.Provider value={{ ...data, ...actions, identity }}>{children}</AipoContext.Provider>;
};

export const useAipo = () => {
  const context = useContext(AipoContext);
  if (!context) throw new Error("AIPO precisa do AipoProvider.");
  return context;
};
